import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { useTodos, useCreateTodo, useUpdateTodo, useDeleteTodo } from '../hooks/useTodos';
import { useCategories } from '../hooks/useCategories';
import { useTimeLogs } from '../hooks/useTimeLogs';
import { ToDoItem, CreateToDoItem } from '../types/api';
import { useToast } from '../components/ToastProvider';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { getApiErrorMessage } from '../utils/errors';
import { formatDateTime, formatDuration, isOverdue, parseDurationToSeconds, toDateTimeLocalValue } from '../utils/datetime';

const PRIORITIES: { value: 1 | 2 | 3; label: string; color: 'error' | 'warning' | 'info' }[] = [
  { value: 1, label: 'High', color: 'error' },
  { value: 2, label: 'Medium', color: 'warning' },
  { value: 3, label: 'Low', color: 'info' },
];

const priorityMeta = (p: number) => PRIORITIES.find((x) => x.value === p) ?? PRIORITIES[2];

type StatusFilter = 'all' | 'active' | 'done';

interface TaskFormState {
  title: string;
  description: string;
  priority: 1 | 2 | 3;
  dueDate: string; // datetime-local value, '' = none
  category: number | ''; // '' = none
}

const emptyForm: TaskFormState = { title: '', description: '', priority: 2, dueDate: '', category: '' };

const TasksPage: React.FC = () => {
  const { data: todos, isLoading, isError, error, refetch } = useTodos();
  const { data: categories } = useCategories();
  const { data: timeLogs } = useTimeLogs();
  const createTodo = useCreateTodo();
  const updateTodo = useUpdateTodo();
  const deleteTodo = useDeleteTodo();
  const { showToast } = useToast();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<number | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ToDoItem | null>(null);
  const [form, setForm] = useState<TaskFormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<ToDoItem | null>(null);

  const filtered = useMemo(() => {
    let items = todos ?? [];
    if (statusFilter !== 'all') items = items.filter((t) => t.completed === (statusFilter === 'done'));
    if (priorityFilter !== '') items = items.filter((t) => t.priority === priorityFilter);
    if (categoryFilter !== '') items = items.filter((t) => t.category === categoryFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      items = items.filter(
        (t) => t.title.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q)
      );
    }
    return [...items].sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      const aDue = a.due_date ? new Date(a.due_date).getTime() : Infinity;
      const bDue = b.due_date ? new Date(b.due_date).getTime() : Infinity;
      if (aDue !== bDue) return aDue - bDue;
      return a.priority - b.priority;
    });
  }, [todos, statusFilter, priorityFilter, categoryFilter, search]);

  // Total seconds logged against each task (via the time log's linked task).
  const timeByTask = useMemo(() => {
    const totals = new Map<number, number>();
    for (const log of timeLogs ?? []) {
      if (log.belongs_to_todo != null) {
        totals.set(
          log.belongs_to_todo,
          (totals.get(log.belongs_to_todo) ?? 0) + parseDurationToSeconds(log.time_logged)
        );
      }
    }
    return totals;
  }, [timeLogs]);

  const activeCount = (todos ?? []).filter((t) => !t.completed).length;

  const openCreate = () => {
    setEditingTask(null);
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (task: ToDoItem) => {
    setEditingTask(task);
    setForm({
      title: task.title,
      description: task.description ?? '',
      priority: task.priority,
      dueDate: toDateTimeLocalValue(task.due_date),
      category: task.category ?? '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const payload: CreateToDoItem = {
      title: form.title.trim(),
      description: form.description.trim(),
      priority: form.priority,
      due_date: form.dueDate ? new Date(form.dueDate).toISOString() : null,
      category: form.category === '' ? null : form.category,
    };

    const onError = (err: unknown) => setFormError(getApiErrorMessage(err));

    if (editingTask) {
      updateTodo.mutate(
        { id: editingTask.id, data: payload },
        {
          onSuccess: () => {
            setDialogOpen(false);
            showToast('Task updated');
          },
          onError,
        }
      );
    } else {
      createTodo.mutate(payload, {
        onSuccess: () => {
          setDialogOpen(false);
          showToast('Task created');
        },
        onError,
      });
    }
  };

  const toggleComplete = (task: ToDoItem) => {
    updateTodo.mutate(
      { id: task.id, data: { completed: !task.completed } },
      { onError: (err) => showToast(getApiErrorMessage(err), 'error') }
    );
  };

  const confirmDelete = () => {
    if (!taskToDelete) return;
    deleteTodo.mutate(taskToDelete.id, {
      onSuccess: () => {
        setTaskToDelete(null);
        showToast('Task deleted');
      },
      onError: (err) => {
        setTaskToDelete(null);
        showToast(getApiErrorMessage(err), 'error');
      },
    });
  };

  const saving = createTodo.isPending || updateTodo.isPending;
  const hasFilters = !!search || statusFilter !== 'all' || priorityFilter !== '' || categoryFilter !== '';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1080, width: '100%', mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">Tasks</Typography>
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading…' : `${activeCount} open · ${(todos ?? []).length} total`}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
          New task
        </Button>
      </Stack>

      {/* Filters */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
        alignItems={{ sm: 'center' }}
      >
        <TextField
          placeholder="Search tasks…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { sm: 240 }, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
          sx={{ bgcolor: 'background.paper' }}
        >
          <ToggleButton value="all" sx={{ px: 2 }}>All</ToggleButton>
          <ToggleButton value="active" sx={{ px: 2 }}>Open</ToggleButton>
          <ToggleButton value="done" sx={{ px: 2 }}>Done</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="Priority"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">Any</MenuItem>
          {PRIORITIES.map((p) => (
            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Any</MenuItem>
          {(categories ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* Content */}
      {isError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
        >
          {getApiErrorMessage(error, 'Could not load your tasks.')}
        </Alert>
      ) : isLoading ? (
        <Stack spacing={1.5}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={86} sx={{ borderRadius: 3.5 }} />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ChecklistRoundedIcon />}
            title={hasFilters ? 'No tasks match your filters' : 'No tasks yet'}
            description={
              hasFilters
                ? 'Try a different search or clear the filters.'
                : 'Create your first task — or ask the AI assistant to do it for you.'
            }
            actionLabel={hasFilters ? undefined : 'Create a task'}
            onAction={hasFilters ? undefined : openCreate}
          />
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((task) => {
            const meta = priorityMeta(task.priority);
            const overdue = isOverdue(task.due_date, task.completed);
            const loggedSeconds = timeByTask.get(task.id) ?? 0;
            return (
              <Card
                key={task.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  transition: 'box-shadow 120ms ease',
                  '&:hover': { boxShadow: '0 4px 16px rgba(31,35,51,0.08)' },
                  opacity: task.completed ? 0.7 : 1,
                }}
              >
                <Tooltip title={task.completed ? 'Mark as open' : 'Mark as done'}>
                  <Checkbox
                    checked={task.completed}
                    onChange={() => toggleComplete(task)}
                    sx={{ mt: 0.25 }}
                    inputProps={{ 'aria-label': `Mark "${task.title}" ${task.completed ? 'open' : 'done'}` }}
                  />
                </Tooltip>
                <Box sx={{ flexGrow: 1, minWidth: 0, pt: 0.75 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      textDecoration: task.completed ? 'line-through' : 'none',
                      wordBreak: 'break-word',
                    }}
                  >
                    {task.title}
                  </Typography>
                  {task.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      {task.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                    <Chip
                      size="small"
                      icon={<FlagRoundedIcon />}
                      label={meta.label}
                      color={meta.color}
                      variant="outlined"
                    />
                    {task.category_name && (
                      <Chip size="small" icon={<FolderRoundedIcon />} label={task.category_name} variant="outlined" />
                    )}
                    {task.due_date && (
                      <Chip
                        size="small"
                        icon={<EventRoundedIcon />}
                        label={`${overdue ? 'Overdue · ' : ''}${formatDateTime(task.due_date)}`}
                        color={overdue ? 'error' : 'default'}
                        variant={overdue ? 'filled' : 'outlined'}
                      />
                    )}
                    {loggedSeconds > 0 && (
                      <Tooltip title="Time logged against this task">
                        <Chip
                          size="small"
                          icon={<ScheduleRoundedIcon />}
                          label={formatDuration(loggedSeconds)}
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
                <Stack direction="row" sx={{ pt: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(task)} aria-label={`Edit ${task.title}`}>
                      <EditRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setTaskToDelete(task)}
                      aria-label={`Delete ${task.title}`}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editingTask ? 'Edit task' : 'New task'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2.25} sx={{ mt: 0.5 }}>
              {formError && (
                <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{formError}</Alert>
              )}
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                required
                fullWidth
                autoFocus
                inputProps={{ maxLength: 200 }}
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Priority"
                  value={form.priority}
                  onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) as 1 | 2 | 3 }))}
                  fullWidth
                >
                  {PRIORITIES.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Category"
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value === '' ? '' : Number(e.target.value) }))
                  }
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  {(categories ?? []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label="Due date"
                type="datetime-local"
                value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving || !form.title.trim()}>
              {saving ? 'Saving…' : editingTask ? 'Save changes' : 'Create task'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!taskToDelete}
        title="Delete task?"
        message={
          <>
            “{taskToDelete?.title}” and its linked time logs will be permanently deleted. This cannot be undone.
          </>
        }
        loading={deleteTodo.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setTaskToDelete(null)}
      />
    </Box>
  );
};

export default TasksPage;
